/**
 * Restrain: a discipline rule that freezes position SIZE while you're in a
 * position. No adds, no partial exits, no reversals. Flatten (exact full size),
 * TP/SL create/move/cancel and any cancel stay allowed. Resets when flat.
 *
 * tradovate-service is authoritative (it refuses size-changing manual orders
 * with code RESTRAIN_ACTIVE). This store only MIRRORS its state so the UI can
 * show a countdown and grey out the controls that would be refused anyway:
 *
 * - `rms:restrain` { enabled, minutes, venues, positions[] } is broadcast on
 *   every change and is the reply to `rms:get-restrain` (sent on
 *   connect/reconnect). The discipline status may also carry the same shape
 *   under `restrain`.
 * - Two independent venues: `futures` (Tradovate-family brokers) and `perps`
 *   (Vest), each with its own on/off, minutes, lock and one-shot
 *   (`venues.futures` / `venues.perps`). Top-level enabled/minutes = futures.
 *   The RST button controls futures; Discipline settings shows both.
 * - An older backend never sends either: `loaded` stays false, `positions`
 *   stays empty, and nothing is badged or gated.
 *
 * The settings themselves (restrainFuturesEnabled / restrainFuturesMinutes,
 * restrainPerpsEnabled / restrainPerpsMinutes) live in the discipline
 * settings (disciplineStore) and are saved through the existing
 * `rms:discipline-update-settings` path.
 *
 * Cancelled-resting-orders notes (server cancels working non-bracket orders
 * when a restrained position opens). Accepted shapes, all optional:
 * - payload.notes: Array<RestrainNote>
 * - payload.note:  RestrainNote | string
 * - positions[i].cancelledOrders: number
 * where RestrainNote = { brokerId?, accountId?, symbol?, cancelled?|count?,
 * message?, at? }. Each distinct note is toasted once per session.
 */
import { create } from 'zustand';
import { addToast } from './toastStore';
import { isHiddenBrokerId } from './brokerVisibility';
import { contractSymbolFor, isPlaceholderSymbol } from './contractSymbols';
import type { WebSocketService } from '../services/WebSocketService';

export const RESTRAIN_MAX_MINUTES = 480;
/** Duration for a user who never saved one (missing key). 0 is a real value: until flat. */
export const RESTRAIN_DEFAULT_MINUTES = 120;
export const RESTRAIN_ERROR_CODE = 'RESTRAIN_ACTIVE';
/** Settings save refused: a running restrain can't be turned off or changed. */
export const RESTRAIN_LOCKED_CODE = 'RESTRAIN_LOCKED';

/** The two independent Restrain rules. */
export type RestrainVenue = 'futures' | 'perps';
export const RESTRAIN_VENUES: readonly RestrainVenue[] = ['futures', 'perps'];

/** The discipline-settings keys of each venue. */
export const RESTRAIN_VENUE_KEYS = {
  futures: { enabled: 'restrainFuturesEnabled', minutes: 'restrainFuturesMinutes' },
  perps: { enabled: 'restrainPerpsEnabled', minutes: 'restrainPerpsMinutes' },
} as const;

/** One venue as the server reports it (`rms:restrain` `venues.<venue>`). */
export interface RestrainVenueState {
  enabled: boolean;
  minutes: number;
  /** A restrain of this venue is running: off / duration changes are refused. */
  locked: boolean;
  activeCount: number;
  /** When the lock lifts; null = until flat (or not locked). */
  endsAt: number | null;
}

export interface RestrainPosition {
  brokerId: string;
  accountId: number | string;
  symbol: string;
  startedAt: number;
  /** null = until flat */
  endsAt: number | null;
  /** Which venue's rule restrains it (missing from an older backend: judged by broker / market). */
  venue?: RestrainVenue;
  cancelledOrders?: number;
  /** Server: its position can't be seen (broker removed / account gone / down); it locks and gates nothing. */
  dormant?: boolean;
}

export interface RestrainNote {
  type?: string;
  kind?: string;
  brokerId?: string;
  accountId?: number | string;
  symbol?: string;
  cancelled?: number;
  count?: number;
  message?: string;
  at?: number;
}

export interface RestrainPayload {
  enabled: boolean;
  minutes: number;
  venues?: Partial<Record<RestrainVenue, Partial<RestrainVenueState>>>;
  positions: RestrainPosition[];
  notes?: RestrainNote[];
  note?: RestrainNote | string;
}

interface RestrainState {
  /** Server has answered at least once (false = older backend / not yet). */
  loaded: boolean;
  /** = venues.futures.enabled */
  enabled: boolean;
  /** = venues.futures.minutes */
  minutes: number;
  venues: Record<RestrainVenue, RestrainVenueState>;
  positions: RestrainPosition[];
  /** The engine's merged `rms:restrain` (every tradovate-service instance): authoritative. */
  applyRestrain: (payload: unknown) => void;
  /**
   * The `restrain` riding on `rms:discipline-status`. That status is PER
   * INSTANCE (the engine relays each tradovate-service's own, unmerged), so an
   * instance with no positions sends an empty list. It only seeds the store
   * until the first merged `rms:restrain` lands, adding entries and never
   * clearing any; after that it is ignored.
   */
  applyRestrainStatus: (payload: unknown) => void;
  reset: () => void;
}

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

/** Contract symbol without exchange suffix, uppercased (MNQZ6.CME -> MNQZ6). */
export const normalizeRestrainSymbol = (s: string | null | undefined): string =>
  (s ?? '').trim().replace(/\.(CME|CBOT|NYMEX|COMEX|NYBOT|KCBT|MGE)$/i, '').toUpperCase();

export const clampRestrainMinutes = (m: unknown): number => {
  const n = Math.round(Number(m));
  if (!Number.isFinite(n)) return 0;
  return Math.min(RESTRAIN_MAX_MINUTES, Math.max(0, n));
};

/** 150 -> "2h 30m", 45 -> "45m", 0 -> "until flat". */
export const formatRestrainMinutes = (m: number): string => {
  const mins = clampRestrainMinutes(m);
  if (mins === 0) return 'until flat';
  const h = Math.floor(mins / 60);
  const r = mins % 60;
  if (h === 0) return `${r}m`;
  return r === 0 ? `${h}h` : `${h}h ${r}m`;
};

/** Quick durations for the RST menu and the settings chips. 0 = until flat. */
export const RESTRAIN_PRESETS: ReadonlyArray<{ minutes: number; label: string }> = [
  { minutes: 5, label: '5m' },
  { minutes: 15, label: '15m' },
  { minutes: 30, label: '30m' },
  { minutes: 60, label: '1h' },
  { minutes: 120, label: '2h' },
  { minutes: 240, label: '4h' },
  { minutes: 480, label: '8h' },
  { minutes: 0, label: 'Until flat' },
];

/** Compact duration for the RST button: 30 -> "30m", 120 -> "2h", 150 -> "2h30m", 0 -> "flat". */
export const formatRestrainShort = (m: number): string => {
  const mins = clampRestrainMinutes(m);
  if (mins === 0) return 'flat';
  const h = Math.floor(mins / 60);
  const r = mins % 60;
  if (h === 0) return `${r}m`;
  return r === 0 ? `${h}h` : `${h}h${r}m`;
};

/** RST button text: "RST" when off, "RST 30m" / "RST 2h" / "RST flat" when on. */
export const restrainButtonLabel = (enabled: boolean, minutes: number): string =>
  enabled ? `RST ${formatRestrainShort(minutes)}` : 'RST';

/** minutes -> "h:mm" for the duration input (150 -> "2:30"). */
export const minutesToHmm = (m: number): string => {
  const mins = clampRestrainMinutes(m);
  return `${Math.floor(mins / 60)}:${String(mins % 60).padStart(2, '0')}`;
};

/**
 * Parse the duration input. Accepts "h:mm" ("2:30"), plain minutes ("150"),
 * or "2h 30m" / "2h" / "45m". Returns null when it isn't a duration at all;
 * otherwise clamps into 0..480.
 */
export const parseRestrainDuration = (raw: string): number | null => {
  const s = raw.trim().toLowerCase();
  if (s === '') return null;
  let m: RegExpMatchArray | null;
  if ((m = s.match(/^(\d{1,2}):(\d{1,2})$/))) {
    return clampRestrainMinutes(Number(m[1]) * 60 + Number(m[2]));
  }
  if ((m = s.match(/^(\d+)$/))) return clampRestrainMinutes(Number(m[1]));
  if ((m = s.match(/^(?:(\d+)\s*h)?\s*(?:(\d+)\s*m)?$/)) && (m[1] || m[2])) {
    return clampRestrainMinutes(Number(m[1] ?? 0) * 60 + Number(m[2] ?? 0));
  }
  return null;
};

/** Remaining time: "4:12" under an hour, "1:02:03" at/over an hour. */
export const formatRestrainRemaining = (ms: number): string => {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
};

const hhmmss = (ms: number): string => {
  const d = new Date(ms);
  return [d.getHours(), d.getMinutes(), d.getSeconds()].map(n => String(n).padStart(2, '0')).join(':');
};

/** Same wording as the server refusal, for tooltips and the client mirror. */
export const restrainLockMessage = (entry: Pick<RestrainPosition, 'endsAt'>): string =>
  entry.endsAt == null
    ? 'Restrain: size locked until flat - you can still exit or move TP/SL'
    : `Restrain: size locked until ${hhmmss(entry.endsAt)} - you can still exit or move TP/SL`;

export const isRestrainActive = (entry: RestrainPosition, now: number = Date.now()): boolean =>
  !entry.dormant && (entry.endsAt == null || entry.endsAt > now);

/** A restrain's venue: its own `venue`, else (older backend) Vest / a `-PERP` market = perps. */
export const restrainVenueOf = (p: Pick<RestrainPosition, 'brokerId' | 'symbol' | 'venue'>): RestrainVenue =>
  p.venue === 'perps' || p.venue === 'futures'
    ? p.venue
    : p.brokerId === 'vest' || /-PERP$/i.test(p.symbol ?? '') ? 'perps' : 'futures';

/**
 * The restrain that locks the settings (server: RestrainService.lockedBy):
 * any until-flat one, else the one that ends last. Null = nothing running
 * (off, or armed and waiting for a position). `venue`: only that venue's
 * restrains count (each venue locks only its own settings).
 */
export function restrainLockOf(
  positions: RestrainPosition[],
  now: number = Date.now(),
  venue?: RestrainVenue,
): { symbol: string; endsAt: number | null } | null {
  let pick: RestrainPosition | null = null;
  for (const p of positions) {
    if (!isRestrainActive(p, now)) continue;
    if (venue && restrainVenueOf(p) !== venue) continue;
    if (!pick || (pick.endsAt !== null && (p.endsAt === null || p.endsAt > pick.endsAt))) pick = p;
  }
  return pick ? { symbol: normalizeRestrainSymbol(pick.symbol), endsAt: pick.endsAt } : null;
}

/** "locked until flat" / "locked - 1:42:10 left". */
export const restrainLockLabel = (lock: { endsAt: number | null }, now: number = Date.now()): string =>
  lock.endsAt == null ? 'locked until flat' : `locked - ${formatRestrainRemaining(lock.endsAt - now)} left`;

/** The active restrain for one position (brokerId + accountId + contract), or null. */
export function findRestrain(
  positions: RestrainPosition[],
  brokerId: string | null | undefined,
  accountId: number | string | null | undefined,
  symbol: string | null | undefined,
  now: number = Date.now(),
): RestrainPosition | null {
  if (!brokerId || accountId == null || !symbol) return null;
  const sym = normalizeRestrainSymbol(symbol);
  const acct = String(accountId);
  return positions.find(p =>
    p.brokerId === brokerId &&
    String(p.accountId) === acct &&
    normalizeRestrainSymbol(p.symbol) === sym &&
    isRestrainActive(p, now),
  ) ?? null;
}

/** A displayed position, as far as restrain matching cares (futures or Vest). */
export interface RestrainPositionLike {
  brokerId?: string | null;
  accountId?: number | string | null;
  symbol?: string | null;
  /** Tradovate: often the only contract identity a fresh position frame carries. */
  contractId?: number | string | null;
  netPos?: number | null;
}

/** Where the position is shown: fallbacks for what the position itself lacks. */
export interface RestrainKeyContext {
  /** Broker the panel acts on (used when the position carries no brokerId). */
  brokerId?: string | null;
  /** Selected account (used when the position carries no accountId). */
  accountId?: number | string | null;
  /** The chart's symbol - the position is on the chart's contract when it names none. */
  chartSymbol?: string | null;
}

export interface RestrainKey {
  brokerId: string | null;
  accountId: number | string | null;
  /** Normalised contract symbol, '' when nothing names the contract. */
  symbol: string;
}

/**
 * The contract a position is on, normalised (MNQZ6.CME -> MNQZ6): its own
 * symbol, else the contractId -> symbol cache, else the chart's symbol. ''
 * when none of them names a contract.
 */
export function restrainSymbolForPosition(
  position: RestrainPositionLike | null | undefined,
  chartSymbol?: string | null,
): string {
  if (position && !isPlaceholderSymbol(position.symbol)) return normalizeRestrainSymbol(position.symbol);
  const cached = position ? contractSymbolFor(position.contractId) : undefined;
  if (cached) return normalizeRestrainSymbol(cached);
  return isPlaceholderSymbol(chartSymbol) ? '' : normalizeRestrainSymbol(chartSymbol);
}

/** brokerId + accountId + contract a restrain on this position is keyed by. */
export function restrainKeyForPosition(
  position: RestrainPositionLike | null | undefined,
  ctx: RestrainKeyContext = {},
): RestrainKey {
  return {
    brokerId: position?.brokerId || ctx.brokerId || null,
    accountId: position?.accountId ?? ctx.accountId ?? null,
    symbol: restrainSymbolForPosition(position, ctx.chartSymbol),
  };
}

/** A full futures contract (root + month code + year): MNQZ6, ESH27. */
const CONTRACT_MONTH = /[FGHJKMNQUVXZ]\d{1,2}$/;

/**
 * The active restrain on a displayed position, keyed by brokerId + accountId +
 * contract. The contract comes from the position itself or the contractId
 * cache; when neither names it yet (a fresh Tradovate frame with only a
 * contractId) the chart's symbol is tried, and failing that the ONE active
 * restrain on that broker account is taken - never a guess between two. A
 * base symbol ("MNQ") matches the one restrain on a contract of that root.
 */
export function findRestrainForPosition(
  positions: RestrainPosition[],
  position: RestrainPositionLike | null | undefined,
  ctx: RestrainKeyContext = {},
  now: number = Date.now(),
): RestrainPosition | null {
  const own = restrainSymbolForPosition(position, null);
  const key = restrainKeyForPosition(position, ctx);
  if (!key.brokerId || key.accountId == null) return null;
  if (key.symbol) {
    const exact = findRestrain(positions, key.brokerId, key.accountId, key.symbol, now);
    // The position's own contract is authoritative: a different one is no match.
    if (exact || (own && CONTRACT_MONTH.test(own))) return exact;
  }
  const root = own || (key.symbol && !CONTRACT_MONTH.test(key.symbol) ? key.symbol : '');
  const acct = String(key.accountId);
  const candidates = positions.filter(p =>
    p.brokerId === key.brokerId &&
    String(p.accountId) === acct &&
    isRestrainActive(p, now) &&
    (!root || normalizeRestrainSymbol(p.symbol).replace(CONTRACT_MONTH, '') === root),
  );
  return candidates.length === 1 ? candidates[0] : null;
}

/**
 * Would this order change the size of a position of `netPos`? Same-side adds
 * and opposite-side orders of any size other than the exact full size do.
 * Flat positions are never restrained, so nothing changes "their" size.
 */
export function wouldChangeSize(netPos: number, action: 'Buy' | 'Sell', quantity: number): boolean {
  if (!netPos) return false;
  const sameSide = (netPos > 0 && action === 'Buy') || (netPos < 0 && action === 'Sell');
  return sameSide || Math.abs(quantity) !== Math.abs(netPos);
}

/**
 * Client mirror of the server's order gate. Returns the refusal message when
 * the order would change a restrained position's size, else null.
 */
export function checkRestrainOrder(args: {
  brokerId: string | null | undefined;
  accountId: number | string | null | undefined;
  symbol: string | null | undefined;
  /** The position's contractId (futures), for when it names no symbol yet. */
  contractId?: number | string | null;
  /** The order's symbol, used when the position names no contract. */
  fallbackSymbol?: string | null;
  netPos: number;
  action: 'Buy' | 'Sell';
  quantity: number;
}): string | null {
  if (!args.netPos) return null;
  const entry = findRestrainForPosition(
    useRestrainStore.getState().positions,
    { brokerId: args.brokerId, accountId: args.accountId, symbol: args.symbol, contractId: args.contractId },
    { chartSymbol: args.fallbackSymbol },
  );
  if (!entry) return null;
  return wouldChangeSize(args.netPos, args.action, args.quantity) ? restrainLockMessage(entry) : null;
}

// ---------------------------------------------------------------------------
// Payload parsing
// ---------------------------------------------------------------------------

const asPosition = (raw: unknown): RestrainPosition | null => {
  const p = raw as Partial<RestrainPosition> | null;
  if (!p || typeof p !== 'object') return null;
  if (typeof p.brokerId !== 'string' || p.accountId == null || typeof p.symbol !== 'string') return null;
  const startedAt = Number(p.startedAt);
  const endsAt = p.endsAt == null ? null : Number(p.endsAt);
  if (endsAt !== null && !Number.isFinite(endsAt)) return null;
  return {
    brokerId: p.brokerId,
    accountId: p.accountId,
    symbol: p.symbol,
    startedAt: Number.isFinite(startedAt) ? startedAt : Date.now(),
    endsAt,
    venue: restrainVenueOf(p as RestrainPosition),
    ...(typeof p.cancelledOrders === 'number' ? { cancelledOrders: p.cancelledOrders } : {}),
    ...(p.dormant === true ? { dormant: true } : {}),
  };
};

const defaultVenue = (): RestrainVenueState => ({
  enabled: false, minutes: RESTRAIN_DEFAULT_MINUTES, locked: false, activeCount: 0, endsAt: null,
});

const defaultVenues = (): Record<RestrainVenue, RestrainVenueState> => ({ futures: defaultVenue(), perps: defaultVenue() });

/**
 * Both venues out of a payload. `venues.<venue>` when the server sends it;
 * an older backend has one toggle for both, so its top-level enabled/minutes
 * fill each venue and the lock is worked out from that venue's positions.
 */
export function parseRestrainVenues(
  p: Partial<RestrainPayload>,
  positions: RestrainPosition[],
  now: number = Date.now(),
): Record<RestrainVenue, RestrainVenueState> {
  const out = defaultVenues();
  for (const venue of RESTRAIN_VENUES) {
    const raw = (p.venues && typeof p.venues === 'object' ? p.venues[venue] : undefined) as Partial<RestrainVenueState> | undefined;
    const enabled = typeof raw?.enabled === 'boolean' ? raw.enabled : p.enabled === true;
    const minutesRaw = typeof raw?.minutes === 'number' ? raw.minutes : p.minutes;
    const minutes = typeof minutesRaw === 'number' ? clampRestrainMinutes(minutesRaw) : RESTRAIN_DEFAULT_MINUTES;
    if (raw && typeof raw.locked === 'boolean') {
      const endsAt = raw.endsAt == null ? null : Number(raw.endsAt);
      out[venue] = {
        enabled,
        minutes,
        locked: raw.locked,
        activeCount: Number.isFinite(Number(raw.activeCount)) ? Number(raw.activeCount) : 0,
        endsAt: endsAt !== null && Number.isFinite(endsAt) ? endsAt : null,
      };
    } else {
      const mine = positions.filter(x => restrainVenueOf(x) === venue && isRestrainActive(x, now));
      const lock = enabled ? restrainLockOf(mine, now) : null;
      out[venue] = { enabled, minutes, locked: lock !== null, activeCount: enabled ? mine.length : 0, endsAt: lock ? lock.endsAt : null };
    }
  }
  return out;
}

const seenNotes = new Set<string>();
/** Notes older than this are history (e.g. a replay after reload), not news. */
const NOTE_MAX_AGE_MS = 2 * 60 * 1000;

const toastNote = (note: RestrainNote | string, fallbackKey: string): void => {
  if (typeof note === 'string') {
    if (!note.trim() || seenNotes.has(`s:${note}`)) return;
    seenNotes.add(`s:${note}`);
    addToast('warning', note, 6000);
    return;
  }
  if (!note || typeof note !== 'object') return;
  if (note.brokerId && isHiddenBrokerId(note.brokerId)) return;
  if (typeof note.at === 'number' && Date.now() - note.at > NOTE_MAX_AGE_MS) return;
  const n = Number(note.cancelled ?? note.count ?? 0);
  if (!note.message && !(n > 0)) return;
  const key = `n:${note.brokerId ?? ''}:${note.accountId ?? ''}:${note.symbol ?? ''}:${note.at ?? fallbackKey}:${n}:${note.message ?? ''}`;
  if (seenNotes.has(key)) return;
  seenNotes.add(key);
  const sym = normalizeRestrainSymbol(note.symbol) || 'position';
  const message = note.message ?? `Restrain: cancelled ${n} resting order${n === 1 ? '' : 's'} on ${sym}`;
  addToast('warning', message, 6000);
};

/** Test hook: forget which notes were already toasted. */
export const _resetRestrainNotes = (): void => seenNotes.clear();

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

/** A merged rms:restrain has been applied; per-instance status copies are ignored from then on. */
let mergedSeen = false;

const restrainEntryKey = (p: RestrainPosition): string =>
  `${p.brokerId}|${String(p.accountId)}|${normalizeRestrainSymbol(p.symbol)}`;

const toastPositionNotes = (positions: RestrainPosition[]): void => {
  for (const pos of positions) {
    if (pos.cancelledOrders && pos.cancelledOrders > 0) {
      toastNote(
        { brokerId: pos.brokerId, accountId: pos.accountId, symbol: pos.symbol, cancelled: pos.cancelledOrders },
        String(pos.startedAt),
      );
    }
  }
};

export const useRestrainStore = create<RestrainState>((set, get) => ({
  loaded: false,
  enabled: false,
  minutes: RESTRAIN_DEFAULT_MINUTES,
  venues: defaultVenues(),
  positions: [],

  applyRestrain: (payload) => {
    const p = payload as Partial<RestrainPayload> | null;
    if (!p || typeof p !== 'object' || typeof p.enabled !== 'boolean') return;
    const positions = Array.isArray(p.positions)
      ? p.positions.map(asPosition).filter((x): x is RestrainPosition => x !== null)
      : [];
    const venues = parseRestrainVenues(p, positions);
    mergedSeen = true;
    set({
      loaded: true,
      enabled: venues.futures.enabled,
      minutes: venues.futures.minutes,
      venues,
      // Switching a venue off clears its active restrains on the server too;
      // dormant ones (gate nothing) survive it there, so they are kept here.
      positions: positions.filter(x => x.dormant || venues[restrainVenueOf(x)].enabled),
    });

    toastPositionNotes(positions);
    if (Array.isArray(p.notes)) p.notes.forEach((n, i) => toastNote(n, `i${i}`));
    if (p.note) toastNote(p.note, 'single');
  },

  applyRestrainStatus: (payload) => {
    if (mergedSeen) return;
    const p = payload as Partial<RestrainPayload> | null;
    if (!p || typeof p !== 'object' || typeof p.enabled !== 'boolean') return;
    const incoming = Array.isArray(p.positions)
      ? p.positions.map(asPosition).filter((x): x is RestrainPosition => x !== null)
      : [];
    const prev = get();
    const incomingVenues = parseRestrainVenues(p, incoming);
    // Union by broker + account + contract: one instance's copy never drops
    // another instance's entries (an instance with no positions sends []).
    const byKey = new Map(prev.positions.map(x => [restrainEntryKey(x), x] as const));
    const kept = incoming.filter(x => incomingVenues[restrainVenueOf(x)].enabled);
    kept.forEach(x => byKey.set(restrainEntryKey(x), x));
    const positions = Array.from(byKey.values());
    // Per venue: on when any instance says on; the lock from the union.
    const venues = defaultVenues();
    for (const venue of RESTRAIN_VENUES) {
      const was = prev.venues[venue];
      const inc = incomingVenues[venue];
      const enabled = prev.loaded ? was.enabled || inc.enabled : inc.enabled;
      const mine = positions.filter(x => restrainVenueOf(x) === venue && isRestrainActive(x));
      const lock = enabled ? restrainLockOf(mine) : null;
      venues[venue] = {
        enabled,
        minutes: inc.minutes,
        locked: lock !== null || inc.locked,
        activeCount: Math.max(mine.length, inc.activeCount),
        endsAt: lock ? lock.endsAt : inc.endsAt,
      };
    }
    set({
      loaded: true,
      enabled: venues.futures.enabled,
      minutes: venues.futures.minutes,
      venues,
      positions,
    });
    toastPositionNotes(kept);
  },

  reset: () => {
    mergedSeen = false;
    set({ loaded: false, enabled: false, minutes: RESTRAIN_DEFAULT_MINUTES, venues: defaultVenues(), positions: [] });
  },
}));

// ---------------------------------------------------------------------------
// Socket wiring
// ---------------------------------------------------------------------------

let restrainWs: WebSocketService | null = null;
let handlersInitialized = false;

/** Ask the server for the current restrain state (connect / reconnect). */
export function requestRestrainState(): void {
  if (restrainWs?.isConnected()) restrainWs.emit('rms:get-restrain', {});
}

/** Toast a broker:error that is a restrain refusal. Returns true if handled. */
export function handleRestrainError(data: unknown): boolean {
  const d = data as { code?: string; message?: string; error?: string } | null;
  if (!d || d.code !== RESTRAIN_ERROR_CODE) return false;
  addToast('warning', d.message || d.error || 'Restrain: size locked - you can still exit or move TP/SL', 6000);
  return true;
}

/** Toast an rms:discipline-error that is a RESTRAIN_LOCKED refusal (either venue). Returns true if handled. */
export function handleRestrainLockedError(data: unknown): boolean {
  const d = data as { code?: string; message?: string; venue?: string } | null;
  if (!d || d.code !== RESTRAIN_LOCKED_CODE) return false;
  const which = d.venue === 'perps' ? 'Perps ' : d.venue === 'futures' ? 'Futures ' : '';
  const message = d.message || "Restrain is running - it can't be turned off or changed until then";
  addToast('warning', which ? `${which}${message.charAt(0).toLowerCase()}${message.slice(1)}` : message, 6000);
  return true;
}

export function initRestrainSocketHandlers(service: WebSocketService): void {
  restrainWs = service;
  requestRestrainState();
  if (handlersInitialized) return;
  handlersInitialized = true;
  service.on('rms:restrain', (payload: unknown) => useRestrainStore.getState().applyRestrain(payload));
  service.on('broker:error', (data: unknown) => { handleRestrainError(data); });
}

export function resetRestrainSocketHandlers(): void {
  handlersInitialized = false;
}
